import { motion } from "framer-motion";

/**
 * Wraps preview contents (iframe + overlays) in a realistic device shell.
 * - desktop -> laptop mockup (lid + screen + base deck)
 * - mobile  -> phone mockup (bezel + notch)
 * `testid` is applied to the inner screen container so tests/overlays anchor to it.
 */
export const DeviceMock = ({ device, testid, children }) => {
  if (device === "mobile") {
    return (
      <div className="w-full h-full flex items-center justify-center py-2">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25 }}
          className="relative bg-[#0e0e10] rounded-[44px] p-2.5 shadow-[0_30px_90px_-20px_rgba(0,0,0,0.95)] ring-1 ring-white/10"
          style={{ height: "min(840px, 100%)", width: "384px" }}
        >
          <div
            className="relative h-full w-full rounded-[34px] overflow-hidden bg-black"
            data-testid={testid}
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 z-40 h-6 w-32 bg-[#0e0e10] rounded-b-2xl" />
            {children}
          </div>
        </motion.div>
      </div>
    );
  }

  // Desktop laptop mockup (MacBook-style)
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full h-full flex flex-col items-center justify-center"
    >
      <div className="w-full max-w-[1008px] flex flex-col items-center">
        {/* Lid: aluminum body + bezel + screen */}
        <div className="relative w-full rounded-t-[20px] rounded-b-[6px] bg-gradient-to-b from-[#2b2b30] to-[#151518] p-[3px] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.95)]">
          <div className="relative w-full rounded-t-[18px] rounded-b-[4px] bg-[#0a0a0c] pt-6 px-3 pb-3">
            {/* webcam */}
            <div className="absolute top-[9px] left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              <span className="h-[5px] w-[5px] rounded-full bg-[#15151a] ring-1 ring-white/10 relative">
                <span className="absolute inset-[1.5px] rounded-full bg-white/20" />
              </span>
            </div>
            {/* screen (16:10) */}
            <div
              className="relative w-full aspect-[16/10] rounded-[4px] overflow-hidden bg-black ring-1 ring-black"
              data-testid={testid}
            >
              {children}
              {/* subtle screen glare */}
              <div className="pointer-events-none absolute inset-0 z-50 bg-gradient-to-br from-white/[0.06] via-transparent to-transparent" />
            </div>
          </div>
        </div>

        {/* Base deck (wider than lid, with hinge + finger groove) */}
        <div className="relative w-[112%] max-w-[1125px]">
          <div className="h-[18px] rounded-b-[14px] bg-gradient-to-b from-[#c9ccd1] via-[#8b8f96] to-[#3a3c40] shadow-[0_20px_30px_-12px_rgba(0,0,0,0.85)]" />
          {/* front finger groove */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[8px] w-[140px] rounded-b-[12px] bg-gradient-to-b from-[#5b5e64] to-[#2a2c30]" />
          {/* bottom shadow */}
          <div className="mx-auto mt-[1px] h-[3px] w-[70%] rounded-full bg-black/60 blur-[2px]" />
        </div>
      </div>
    </motion.div>
  );
};
